// src/context/VibeGamificationContext.jsx - COMPLETE VERSION with User-Specific Storage
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const VibeGamificationContext = createContext();

const GAMIFICATION_VERSION = '2.0';

// VIBE-SPECIFIC ACHIEVEMENTS
const VIBE_ACHIEVEMENTS = {
  // Getting Started
  first_steps: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Completed your first inner voice exercise',
    emoji: '👶',
    xp: 50,
    category: 'milestone',
    celebrationText: "You've taken your first step toward inner voice mastery!",
  },
  
  assessment_complete: {
    id: 'assessment_complete',
    name: 'Inner Voice Explorer',
    description: 'Completed the Vibe Check Assessment',
    emoji: '🧭',
    xp: 100,
    category: 'milestone',
    celebrationText: "You know your starting point - that's wisdom!",
  },

  // Learning Milestones
  foundation_builder: {
    id: 'foundation_builder',
    name: 'Foundation Builder',
    description: 'Completed 3 foundation modules',
    emoji: '🏗️',
    xp: 200,
    category: 'milestone',
    celebrationText: "You're building a solid foundation for transformation!",
  },

  half_way_hero: {
    id: 'half_way_hero',
    name: 'Halfway Hero',
    description: 'Completed 50% of available modules',
    emoji: '⭐',
    xp: 300,
    category: 'milestone',
    celebrationText: "You're halfway to inner voice mastery!",
  },

  vibe_graduate: {
    id: 'vibe_graduate',
    name: 'Vibe Graduate',
    description: 'Completed all foundation modules',
    emoji: '🎓',
    xp: 500,
    category: 'milestone',
    celebrationText: "You've mastered the fundamentals - you're ready for anything!",
  },

  // Consistency & Streaks
  consistent_learner: {
    id: 'consistent_learner',
    name: 'Consistent Learner',
    description: '7-day learning streak',
    emoji: '📚',
    xp: 150,
    category: 'streak',
    celebrationText: 'Consistency is the key to transformation!',
  },

  week_warrior: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: '7-day activity streak',
    emoji: '🔥',
    xp: 200,
    category: 'streak',
    celebrationText: "One week of consistent growth - you're unstoppable!",
  },

  month_master: {
    id: 'month_master',
    name: 'Month Master',
    description: '30-day streak',
    emoji: '👑',
    xp: 500,
    category: 'streak',
    celebrationText: "30 days of dedication - you're a true transformation champion!",
  },

  // Tool Mastery
  tool_explorer: {
    id: 'tool_explorer',
    name: 'Tool Explorer',
    description: 'Tried 3 different interactive tools',
    emoji: '🛠️',
    xp: 150,
    category: 'exploration',
    celebrationText: "You're exploring all the ways to grow!",
  },

  self_talk_ninja: {
    id: 'self_talk_ninja',
    name: 'Self-Talk Ninja',
    description: 'Completed 10 self-talk exercises',
    emoji: '🥷',
    xp: 250,
    category: 'mastery',
    celebrationText: 'Your inner voice skills are getting sharp!',
  },

  // Special Achievements
  future_architect: {
    id: 'future_architect',
    name: 'Future Architect',
    description: 'Completed a Future Self letter exchange',
    emoji: '🏗️',
    xp: 200,
    category: 'special',
    celebrationText: "You're actively designing your future!",
  },

  helper_heart: {
    id: 'helper_heart',
    name: 'Helper Heart',
    description: 'Helped someone else with their inner voice journey',
    emoji: '💝',
    xp: 300,
    category: 'social',
    celebrationText: 'Teaching others deepens your own understanding!',
  },

  level_legend: {
    id: 'level_legend',
    name: 'Level Legend',
    description: 'Reached Level 10',
    emoji: '🚀',
    xp: 1000,
    category: 'milestone',
    celebrationText: "Level 10! You're in the top tier of inner voice masters!",
  },

  // Daily Challenges
  challenge_champion: {
    id: 'challenge_champion',
    name: 'Challenge Champion',
    description: 'Completed 10 daily challenges',
    emoji: '🏆',
    xp: 250,
    category: 'challenge',
    celebrationText: 'You rise to every challenge!',
  },

  daily_dedication: {
    id: 'daily_dedication',
    name: 'Daily Dedication',
    description: 'Completed daily challenges for 7 days straight',
    emoji: '⚡',
    xp: 350,
    category: 'challenge',
    celebrationText: 'Your daily dedication is transforming your inner voice!',
  },
};

// LEVEL SYSTEM
const LEVEL_SYSTEM = {
  1: { name: 'Fresh Start', emoji: '🌱', xpRequired: 100, vibe: 'Just getting started on your inner voice journey!' },
  2: { name: 'Curious Explorer', emoji: '🔍', xpRequired: 250, vibe: 'You\'re exploring what\'s possible!' },
  3: { name: 'Mindful Observer', emoji: '👁️', xpRequired: 500, vibe: 'You\'re noticing your inner patterns!' },
  4: { name: 'Pattern Breaker', emoji: '⚡', xpRequired: 1000, vibe: 'You\'re breaking old thought habits!' },
  5: { name: 'Voice Shifter', emoji: '🎭', xpRequired: 1750, vibe: 'You\'re actively changing your inner voice!' },
  6: { name: 'Confidence Builder', emoji: '💪', xpRequired: 2750, vibe: 'Your confidence is growing stronger!' },
  7: { name: 'Inner Wisdom', emoji: '🧠', xpRequired: 4000, vibe: 'You\'re developing deep self-awareness!' },
  8: { name: 'Transformation Agent', emoji: '🦋', xpRequired: 5500, vibe: 'You\'re transforming from the inside out!' },
  9: { name: 'Vibe Master', emoji: '✨', xpRequired: 7500, vibe: 'You\'ve mastered your inner landscape!' },
  10: { name: 'Vibe Legend', emoji: '🔥', xpRequired: 10000, vibe: 'You\'re a legend in the inner voice community!' },
};

// Helper function to get current user ID - ADD THIS
const getCurrentUserId = () => {
  try {
    const authData = localStorage.getItem('vibe_user_identity');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.userId;
    }
    return null;
  } catch (error) {
    console.warn('Could not get current user ID for gamification:', error);
    return null;
  }
};

// UPDATED: Get storage key with user ID
const getStorageKey = (baseKey) => {
  const userId = getCurrentUserId();
  return userId ? `${baseKey}_${userId}` : baseKey;
};

export const VibeGamificationProvider = ({ children }) => {
  // Core gamification state
  const [totalXP, setTotalXP] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [completedModules, setCompletedModules] = useState([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [pendingAchievements, setPendingAchievements] = useState([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);
  const [helpedOthers, setHelpedOthers] = useState(0);

  // Load gamification data on mount
  useEffect(() => {
    loadGamificationData();
  }, []);

  // UPDATED: Save gamification data with user-specific keys
  const saveGamificationData = useCallback(() => {
    const userId = getCurrentUserId();
    
    if (!userId) {
      console.log('🚫 No user logged in, skipping gamification save');
      return;
    }

    console.log('💾 Saving gamification data for user:', userId);

    const dataToSave = {
      totalXP,
      currentLevel,
      completedModules,
      unlockedAchievements,
      currentStreak,
      longestStreak,
      helpedOthers,
      lastActivity: new Date().toISOString(),
      version: GAMIFICATION_VERSION,
    };

    try {
      localStorage.setItem(getStorageKey('vibeGamificationData'), JSON.stringify(dataToSave));
      console.log('✅ Gamification data saved for user:', userId);
    } catch (error) {
      console.error('❌ Failed to save gamification data:', error);
    }
  }, [totalXP, currentLevel, completedModules, unlockedAchievements, currentStreak, longestStreak, helpedOthers]);

  // UPDATED: Load gamification data with user-specific keys
  const loadGamificationData = useCallback(() => {
    const userId = getCurrentUserId();
    
    if (!userId) {
      console.log('🚫 No user logged in, using default gamification data');
      setTotalXP(0);
      setCurrentLevel(1);
      setCompletedModules([]);
      setUnlockedAchievements([]);
      setCurrentStreak(0);
      setLongestStreak(0);
      setHelpedOthers(0);
      return;
    }

    console.log('📥 Loading gamification data for user:', userId);

    try {
      const saved = localStorage.getItem(getStorageKey('vibeGamificationData'));
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('✅ Loaded gamification data:', {
          totalXP: parsed.totalXP,
          currentLevel: parsed.currentLevel,
          completedModules: parsed.completedModules?.length || 0,
        });
        
        setTotalXP(parsed.totalXP || 0);
        setCurrentLevel(parsed.currentLevel || 1);
        setCompletedModules(parsed.completedModules || []);
        setUnlockedAchievements(parsed.unlockedAchievements || []);
        setCurrentStreak(parsed.currentStreak || 0);
        setLongestStreak(parsed.longestStreak || 0);
        setHelpedOthers(parsed.helpedOthers || 0);
        setLastActivity(parsed.lastActivity);
        
        console.log('🔄 Gamification data refreshed from localStorage');
        return;
      }
    } catch (error) {
      console.error('❌ Failed to load gamification data:', error);
    }

    console.log('📝 Using default gamification data for new user');
    setTotalXP(0);
    setCurrentLevel(1);
    setCompletedModules([]);
    setUnlockedAchievements([]);
    setCurrentStreak(0);
    setLongestStreak(0);
    setHelpedOthers(0);
  }, []);

  // Auto-save when data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveGamificationData();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [saveGamificationData]);

  // Calculate current level data
  const getCurrentLevelData = useCallback(() => {
    return LEVEL_SYSTEM[currentLevel] || LEVEL_SYSTEM[1];
  }, [currentLevel]);

  // Calculate XP needed for next level
  const getXPToNextLevel = useCallback(() => {
    const nextLevel = currentLevel + 1;
    const nextLevelData = LEVEL_SYSTEM[nextLevel];
    if (!nextLevelData) return 0;
    
    return Math.max(0, nextLevelData.xpRequired - totalXP);
  }, [currentLevel, totalXP]);

  // Add XP and check for level ups
  const addXP = useCallback((amount, reason = '') => {
    console.log(`💰 Adding ${amount} XP: ${reason}`);
    
    const newTotalXP = totalXP + amount;
    setTotalXP(newTotalXP);
    
    // Check for level up
    let newLevel = currentLevel;
    while (LEVEL_SYSTEM[newLevel + 1] && newTotalXP >= LEVEL_SYSTEM[newLevel + 1].xpRequired) {
      newLevel++;
    }
    
    if (newLevel > currentLevel) {
      console.log(`🎉 LEVEL UP! ${currentLevel} → ${newLevel}`);
      setCurrentLevel(newLevel);
      
      // Add level up to pending achievements
      const levelUpAchievement = {
        type: 'level_up',
        level: newLevel,
        levelData: LEVEL_SYSTEM[newLevel],
        message: `Welcome to Level ${newLevel}: ${LEVEL_SYSTEM[newLevel].name}!`,
      };
      
      setPendingAchievements(prev => [...prev, levelUpAchievement]);
      
      // Check for level-based achievements
      if (newLevel === 10) {
        unlockAchievement('level_legend');
      }
    }
    
    updateActivity();
  }, [totalXP, currentLevel]);

  // Unlock achievement
  const unlockAchievement = useCallback((achievementId) => {
    if (unlockedAchievements.includes(achievementId)) {
      console.log(`🏆 Achievement ${achievementId} already unlocked`);
      return;
    }
    
    const achievement = VIBE_ACHIEVEMENTS[achievementId];
    if (!achievement) {
      console.warn(`❌ Achievement ${achievementId} not found`);
      return;
    }
    
    console.log(`🎖️ Achievement unlocked: ${achievement.name}`);
    
    setUnlockedAchievements(prev => [...prev, achievementId]);
    
    // Add XP for achievement
    setTotalXP(prev => prev + achievement.xp);
    
    // Add to pending achievements for display
    setPendingAchievements(prev => [...prev, {
      type: 'achievement',
      achievement,
      message: achievement.celebrationText,
    }]);
  }, [unlockedAchievements]);

  // Complete module
  const completeModule = useCallback((moduleId) => {
    console.log(`📚 Completing module: ${moduleId}`);
    
    if (completedModules.includes(moduleId)) {
      console.log(`✅ Module ${moduleId} already completed`);
      return;
    }
    
    setCompletedModules(prev => [...prev, moduleId]);
    
    // Award XP for module completion
    addXP(100, `Completed Module: ${moduleId}`);
    
    // Check for module-based achievements
    const newCompletedCount = completedModules.length + 1;
    
    if (newCompletedCount === 1) {
      unlockAchievement('first_steps');
    } else if (newCompletedCount === 3) {
      unlockAchievement('foundation_builder');
    } else if (newCompletedCount >= 6) {
      unlockAchievement('half_way_hero');
    } else if (newCompletedCount >= 8) {
      unlockAchievement('vibe_graduate');
    }
    
    updateActivity();
  }, [completedModules, addXP, unlockAchievement]);

  // Update activity and streak
  const updateActivity = useCallback(() => {
    const today = new Date();
    const todayString = today.toDateString();
    const lastActivityDate = lastActivity ? new Date(lastActivity).toDateString() : null;
    
    console.log('🔄 Updating activity - Today:', todayString, 'Last:', lastActivityDate, 'Current streak:', currentStreak);
    
    setLastActivity(new Date().toISOString());
    
    if (lastActivityDate === todayString) {
      // Same day, no streak change - but make sure we have at least 1
      if (currentStreak === 0) {
        setCurrentStreak(1);
      }
      return;
    }
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toDateString();
    
    if (lastActivityDate === yesterdayString) {
      // Consecutive day, extend streak
      const newStreak = currentStreak + 1;
      console.log('📈 Extending streak from', currentStreak, 'to', newStreak);
      setCurrentStreak(newStreak);
      
      if (newStreak > longestStreak) {
        setLongestStreak(newStreak);
      }
      
      // Check for streak achievements
      if (newStreak === 7) {
        unlockAchievement('week_warrior');
        unlockAchievement('consistent_learner');
      } else if (newStreak === 30) {
        unlockAchievement('month_master');
      }
    } else if (!lastActivityDate) {
      // First time activity
      console.log('🆕 First activity, starting streak at 1');
      setCurrentStreak(1);
    } else {
      // Streak broken, start new one
      console.log('💔 Streak broken, resetting to 1. Last activity was:', lastActivityDate);
      setCurrentStreak(1);
    }
  }, [lastActivity, currentStreak, longestStreak, unlockAchievement]);

  // Sync module progress from external source
  const syncModuleProgress = useCallback((modules) => {
    console.log('🔄 Syncing module progress:', modules);
    
    if (!modules || !Array.isArray(modules)) {
      console.warn('⚠️ Invalid modules data for sync');
      return;
    }
    
    // Add any new completed modules
    modules.forEach(moduleId => {
      if (!completedModules.includes(moduleId)) {
        console.log('📝 Adding missing completed module:', moduleId);
        completeModule(moduleId);
      }
    });
  }, [completedModules, completeModule]);

  // Help someone (social feature)
  const helpSomeone = useCallback((reason = 'Helped a fellow learner') => {
    console.log(`💝 Help action: ${reason}`);
    
    setHelpedOthers(prev => prev + 1);
    addXP(50, reason);
    
    // Check for helping achievements
    if (helpedOthers + 1 === 1) {
      unlockAchievement('helper_heart');
    }
    
    updateActivity();
  }, [helpedOthers, addXP, unlockAchievement, updateActivity]);

  // Clear pending achievements
  const clearPendingAchievements = useCallback(() => {
    setPendingAchievements([]);
  }, []);

  // Force refresh data (for debugging)
  const refreshGamificationData = useCallback(() => {
    console.log('🔄 Force refreshing gamification data...');
    loadGamificationData();
  }, [loadGamificationData]);

  // Debug function to manually trigger streak update
  const debugUpdateStreak = useCallback(() => {
    console.log('🐛 Debug: Manually triggering streak update');
    console.log('Current state:', { currentStreak, longestStreak, lastActivity });
    updateActivity();
  }, [currentStreak, longestStreak, lastActivity, updateActivity]);

  // ALSO UPDATE: Clear gamification data for specific user
  const clearUserGamificationData = useCallback((userId = null) => {
    const targetUserId = userId || getCurrentUserId();
    
    if (!targetUserId) {
      console.log('🚫 No user ID provided for gamification clear');
      return;
    }

    console.log('🧹 Clearing gamification data for user:', targetUserId);
    
    const keysToClear = [
      `vibeGamificationData_${targetUserId}`,
      `vibe_assessment_results_${targetUserId}`,
      `vibe_progress_data_${targetUserId}`,
      `vibe_preferences_${targetUserId}`,
    ];
    
    keysToClear.forEach(key => {
      localStorage.removeItem(key);
      console.log('❌ Removed:', key);
    });

    // Reset state to defaults
    setTotalXP(0);
    setCurrentLevel(1);
    setCompletedModules([]);
    setUnlockedAchievements([]);
    setCurrentStreak(0);
    setLongestStreak(0);
    setHelpedOthers(0);
    setLastActivity(null);
    setPendingAchievements([]);
  }, []);

  // Context value
  const contextValue = {
    // State
    totalXP,
    currentLevel,
    completedModules,
    unlockedAchievements,
    pendingAchievements,
    currentStreak,
    longestStreak,
    helpedOthers,
    lastActivity,
    
    // Computed values
    currentLevelData: getCurrentLevelData(),
    xpToNextLevel: getXPToNextLevel(),
    
    // Actions
    addXP,
    completeModule,
    unlockAchievement,
    updateStreak: updateActivity,
    helpSomeone,
    syncModuleProgress,
    clearPendingAchievements,
    refreshGamificationData,
    clearUserGamificationData,
    debugUpdateStreak,
    
    // Constants
    VIBE_ACHIEVEMENTS,
    LEVEL_SYSTEM,
    
    // Utilities
    getCurrentUserId,
    getStorageKey,
  };

  return (
    <VibeGamificationContext.Provider value={contextValue}>
      {children}
    </VibeGamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(VibeGamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a VibeGamificationProvider');
  }
  return context;
};

// UPDATED: useVibeGamification alias for backward compatibility
export const useVibeGamification = useGamification;

export default VibeGamificationContext;